import type {
  DividerRuleError,
  DividerRuleErrorCode,
} from "@CommonUtils/pog/dividerPolicyValidation";
import { translate } from "src/i18n/translate";

/**
 * The merchant's length unit. Stored lengths are x100 of whatever unit the
 * source used (a PSA's own units, the fixture feed's), and nothing records
 * which: the PSA import reads no Measurement field and converts nothing. So
 * no unit is printed until OWA stores one; edits stay right either way, as
 * they divide and multiply by the same 100. Callers pass it down as one
 * `unit` value; no string hardcodes it.
 */
export const DEFAULT_LENGTH_UNIT = "";

/** A stored x100 length as a number in the merchant's unit: 150 -> 1.5. */
export const displayLength = (hundredths: number) => Number((hundredths / 100).toFixed(2));

/** A stored x100 length as the merchant reads it: 150 -> "1.5 in", or "1.5" with no unit. */
export const formatLength = (hundredths: number, unit: string) =>
  unit ? `${displayLength(hundredths)} ${unit}` : String(displayLength(hundredths));

/** The divider rule error codes, as the Dividers section's translation keys. */
export const DIVIDER_RULE_ERROR_KEYS = {
  DIVIDER_RULE_INVALID: "planogramRules.dividers.errors.ruleInvalid",
  LOADER_DEPTH_NEGATIVE: "planogramRules.dividers.errors.loaderDepthNegative",
  LOADER_DEPTH_NOT_BELOW_SHELF: "planogramRules.dividers.errors.loaderDepthNotBelowShelf",
  LOADER_DEPTH_NOT_BELOW_SHALLOWEST_SHELF:
    "planogramRules.dividers.errors.loaderDepthNotBelowShallowestShelf",
  LOADER_DEPTH_MISSING: "planogramRules.dividers.errors.loaderDepthMissing",
} as const satisfies Record<DividerRuleErrorCode, string>;

/** A divider rule error in words, its {{depth}} filled by `formatLength`. */
export const formatLengthError = (
  error: Pick<DividerRuleError, "code" | "params">,
  unit: string,
): string =>
  translate("common", DIVIDER_RULE_ERROR_KEYS[error.code], {
    depth: error.params ? formatLength(error.params.depth, unit) : "",
  });
